import React, { memo, useMemo } from "react";
import { toUniversalUtc } from "../../../../utils/chatDownload";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";

const ChatWindow = ({
  messages,
  hasMore,
  isLoadingMore,
  onLoadMore,
  loggedUserId,
  isLocked,
  input,
  setInput,
  onSendMessage,
  onInputKeyDown,
  onFileSelect,
  selectedFiles,
  messagesRef,
  typingUserLabel,
  canChat,
  onDeleteMessage,
}) => {
  const renderedMessages = useMemo(() => {
    const deduped = [];
    const seen = new Set();
    messages.forEach((msg) => {
      const key =
        String(msg?._id || "").trim() ||
        String(msg?.tempId || "").trim() ||
        `${msg?.message || ""}|${msg?.createdAt || ""}|${msg?.senderId?._id || msg?.senderId || ""}`;
      if (!key || seen.has(key)) return;
      seen.add(key);
      deduped.push(msg);
    });
    return deduped;
  }, [messages]);
  const isFileLikeName = (value = "") => {
    const text = String(value || "").trim().toLowerCase();
    return /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|zip|rar|7z|jpg|jpeg|png|gif|webp)$/i.test(text);
  };

  return (
    <div className="w-full flex flex-col bg-gray-50 rounded-r-md min-h-0">
    <div ref={messagesRef} className="flex-1 min-h-0 overflow-auto p-2 space-y-2">
      {hasMore && (
        <div className="text-center">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="text-[11px] px-2 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-60"
          >
            {isLoadingMore ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
      {renderedMessages.map((msg) => {
        const senderId = String(msg?.senderId?._id || msg?.senderId || "");
        const isOwn = senderId === String(loggedUserId || "");
        const isFileMessage = String(msg?.type || "").toLowerCase() === "file";
        const senderName =
          msg?.senderName ||
          (msg?.senderId?.EmpFirstName
            ? `${msg.senderId.EmpFirstName || ""} ${msg.senderId.EmpLastName || ""}`.trim()
            : "Unknown");
        const attachmentEntries = (() => {
          const listed = [
            ...(msg.attachments || []),
            ...(msg.files || []),
            ...(msg.attachmentPayload || []),
            ...((msg.attachmentUrls || []).map((url) => ({ url, fileUrl: url })) || []),
          ];
          if (listed.length > 0) {
            const seen = new Set();
            return listed.filter((file) => {
              const dedupeKey = String(
                file?.fileId || file?.attachmentId || file?.id || file?._id || file?.url || file?.fileUrl || file?.name || file?.fileName || ""
              ).trim();
              if (!dedupeKey) return true;
              if (seen.has(dedupeKey)) return false;
              seen.add(dedupeKey);
              return true;
            });
          }
          const fileUrl = msg?.fileUrl || msg?.url || msg?.filepathurl || msg?.path || msg?.location || "";
          const fileName = msg?.fileName || msg?.filename || msg?.name || msg?.message || "Attachment";
          if (!fileUrl && !fileName) return [];
          return [{ url: fileUrl, fileUrl, fileName, name: fileName, filename: fileName }];
        })();
        const inferredFileAttachment =
          attachmentEntries.length === 0 && isFileLikeName(msg?.message)
            ? [{ fileName: msg?.message, name: msg?.message, filename: msg?.message, url: "", fileUrl: "" }]
            : [];
        const finalAttachmentEntries = attachmentEntries.length > 0 ? attachmentEntries : inferredFileAttachment;
        const normalizedMessageText = String(msg?.message || "").trim().toLowerCase();
        const attachmentNameSet = new Set(
          finalAttachmentEntries
            .map((file) => String(file?.name || file?.fileName || file?.filename || "").trim().toLowerCase())
            .filter(Boolean)
        );
        const shouldHideMessageText =
          finalAttachmentEntries.length > 0 &&
          (isFileMessage || (normalizedMessageText && attachmentNameSet.has(normalizedMessageText)));
        return (
          <div key={msg._id || `${msg.createdAt}-${msg.message}`} className={isOwn ? "text-right" : "text-left"}>
            <div className={`flex items-end ${isOwn ? "justify-end gap-1" : "justify-start"}`}>
              {isOwn && msg?._id ? (
                <button
                  onClick={() => onDeleteMessage?.(msg)}
                  title="Delete message"
                  className="text-red-500 hover:text-red-600 pb-1"
                >
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />
                </button>
              ) : null}
              <div
                className={`inline-block text-xs px-2 py-1 rounded max-w-[80%] break-words ${
                  isOwn ? "bg-blue-600 text-white" : "bg-gray-200 text-black"
                }`}
              >
                <p className={`text-[10px] mb-0.5 ${isOwn ? "text-blue-100" : "text-gray-600"}`}>
                  {isOwn ? "You" : senderName}
                </p>
                {!shouldHideMessageText ? (
                  <p>
                    {msg.message} {msg.pending ? <span className="opacity-70">(sending...)</span> : null}
                    {msg.failed ? <span className="opacity-70">(failed)</span> : null}
                  </p>
                ) : null}
                {finalAttachmentEntries.length > 0 && (
                  <div className="mt-1">
                    {finalAttachmentEntries.map((file, index) => {
                      const fileUrl = file?.url || file?.fileUrl || file?.filepathurl || file?.path || file?.location || "";
                      const fileName = file?.name || file?.fileName || file?.filename || "Attachment";
                      return fileUrl ? (
                        <div key={`${fileName}-${index}`} className="flex items-center gap-2">
                          <span className="truncate">{fileName}</span>
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className={`underline ${isOwn ? "text-white" : "text-blue-700"}`}
                          >
                            Open
                          </a>
                          <a
                            href={fileUrl}
                            download={fileName}
                            className={`underline ${isOwn ? "text-white" : "text-blue-700"}`}
                          >
                            Download
                          </a>
                        </div>
                      ) : (
                        <span key={`${fileName}-${index}`} className="block">
                          {fileName}
                        </span>
                      );
                    })}
                  </div>
                )}
                <p className={`mt-1 text-[10px] ${isOwn ? "text-blue-100" : "text-gray-600"}`}>
                  {toUniversalUtc(msg.createdAt)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
      {typingUserLabel ? (
        <div className="text-[11px] text-gray-500 italic px-1">{typingUserLabel} is typing...</div>
      ) : null}
    </div>

    <div className="p-2 border-t border-gray-200 bg-white">
      <div className="flex gap-2 items-center mb-1">
        <label
          htmlFor="chat-file-input"
          title="Attach files"
          className={`inline-flex items-center justify-center p-1.5 border rounded-md bg-gray-100 ${
            isLocked || !canChat ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-200 cursor-pointer"
          }`}
        >
          <AttachFileOutlinedIcon sx={{ fontSize: 18 }} />
        </label>
        <input
          id="chat-file-input"
          type="file"
          multiple
          onChange={onFileSelect}
          className="hidden"
          disabled={isLocked || !canChat}
        />
        <span className="text-[11px] text-gray-500">{selectedFiles.length} file(s)</span>
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder={!canChat ? "Start chat first" : isLocked ? "Chat is locked" : "Type a message"}
          disabled={isLocked || !canChat}
          className="w-full border rounded p-1 text-xs"
        />
        <button
          onClick={onSendMessage}
          disabled={isLocked || !canChat}
          title="Send message"
          className="inline-flex items-center justify-center p-1.5 rounded bg-blue-600 text-white disabled:opacity-60"
        >
          <SendOutlinedIcon sx={{ fontSize: 18 }} />
        </button>
      </div>
    </div>
    </div>
  );
};

export default memo(ChatWindow);
